import { useState, useEffect } from "react";
import { toast } from "sonner";
import { articlesAPI } from "../../lib/api";

const BG = "#06080F";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export default function InferenceSavedSummaries({ workspaceId }) {
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("All");
  const [filterTag, setFilterTag] = useState("All");
  const [summaries, setSummaries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const res = await articlesAPI.summaries();
        const mapped = (res.data || []).map(s => ({
          id: s.summary_id,
          title: s.article_title || "Untitled article",
          company: s.company_name || "Unknown",
          tags: [],
          date: s.created_at ? timeAgo(s.created_at) : "N/A",
          summary: s.summary || "",
          url: s.article_url || "",
        }));
        setSummaries(mapped);
      } catch (err) {
        console.error("Failed to fetch summaries:", err);
        toast.error("Could not load saved summaries.");
      }
    };
    fetchSummaries();
  }, []);

  const companies = ["All", ...Array.from(new Set(summaries.map(s => s.company)))];
  const allTags = ["All", ...Array.from(new Set(summaries.flatMap(s => s.tags)))];

  const filtered = summaries.filter(s => {
    if (filterCompany !== "All" && s.company !== filterCompany) return false;
    if (filterTag !== "All" && !s.tags.includes(filterTag)) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase()) && !s.company.toLowerCase().includes(search.toLowerCase()) && !s.summary.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const selectStyle = {
    background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
    padding: "7px 12px", fontSize: 12, color: TEXT_PRIMARY, outline: "none",
    fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
  };

  return (
    <div data-testid="saved-summaries-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        select option { background: #0D1117; }
      `}</style>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 28px" }}>
        {/* Title + Search */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em" }}>Saved Summaries</h1>
            <p style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4 }}>{filtered.length} summaries across {companies.length - 1} companies</p>
          </div>
          <input
            data-testid="summaries-search"
            placeholder="Search summaries..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
              padding: "9px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
              fontFamily: "'DM Sans', sans-serif", width: 260,
            }}
            onFocus={e => e.target.style.borderColor = ACCENT}
            onBlur={e => e.target.style.borderColor = BORDER}
          />
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <select data-testid="summaries-company-filter" value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={selectStyle}>
            {companies.map(c => <option key={c} value={c}>{c === "All" ? "All Companies" : c}</option>)}
          </select>
          <select data-testid="summaries-tag-filter" value={filterTag} onChange={e => setFilterTag(e.target.value)} style={selectStyle}>
            {allTags.map(t => <option key={t} value={t}>{t === "All" ? "All Tags" : t}</option>)}
          </select>
        </div>

        {/* Summary list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(s => (
            <div key={s.id} data-testid={`summary-card-${s.id}`}
              onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
              style={{
                background: SURFACE_2, border: `1px solid ${expandedId === s.id ? ACCENT + "44" : BORDER}`,
                borderRadius: 10, padding: "16px 20px", cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { if (expandedId !== s.id) e.currentTarget.style.borderColor = "#374151"; }}
              onMouseLeave={e => { if (expandedId !== s.id) e.currentTarget.style.borderColor = BORDER; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>{s.title}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: ACCENT, fontWeight: 600 }}>{s.company}</span>
                    <span style={{ fontSize: 11, color: TEXT_DIM }}>·</span>
                    <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{s.date}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {s.tags.slice(0, 3).map(t => (
                    <span key={t} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 12, background: `${ACCENT}12`, border: `1px solid ${ACCENT}33`, color: ACCENT }}>{t}</span>
                  ))}
                </div>
              </div>

              {expandedId === s.id ? (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                  <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{s.summary}</p>
                  {s.url && (
                    <a
                      data-testid={`summary-source-${s.id}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ display: "inline-block", marginTop: 12, fontSize: 11, color: ACCENT, fontFamily: "'DM Mono', monospace", textDecoration: "none" }}
                    >
                      VIEW SOURCE →
                    </a>
                  )}
                </div>
              ) : (
                <p style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.summary}
                </p>
              )}
            </div>
          ))}

          {summaries.length === 0 ? (
            <div data-testid="summaries-empty-state" style={{ textAlign: "center", padding: "80px 0" }}>
              <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No AI summaries yet.</p>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>Open <strong style={{ color: TEXT_PRIMARY }}>Saved Articles</strong> and hit <strong style={{ color: TEXT_PRIMARY }}>Summarise</strong> on any article.</p>
            </div>
          ) : filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <p style={{ fontSize: 14, color: TEXT_DIM }}>No summaries found matching your filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
